import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Battery, Sparkles, AlertTriangle, ChevronRight, Zap } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { CQ7_PACKAGES, startingPrice, fmtAUD } from '../data/cq7Packages';
import { GOODWE_PACKAGES_512, GOODWE_PACKAGES_832, startingPrice as goodweStart, fmtAUD as goodweFmt } from '../data/goodwePackages';
import { DEYE_PACKAGES, startingPrice as deyeStart, fmtAUD as deyeFmt } from '../data/deyePackages';

// Curated list of fully-supplied + installed packages.
// Add new packages here as Supa Energy Hub launches them.
const PACKAGES = [
  {
    slug: 'eofy-battery-deal',
    name: 'EOFY Battery Deal — eCactus Agave',
    eyebrow: 'Limited EOFY Offer',
    summary:
      'eCactus Agave SH all-in-one battery package — fully supplied and installed. 10.24 kWh from $3,999 or 20.48 kWh from $4,999 (after rebate, standard install).',
    fromPrice: 'From $3,999',
    suffix: 'installed after rebate*',
    badge: { label: 'Ends 30 June 2026', icon: AlertTriangle, color: 'red' },
    image: '/product-images/whes/agave-sh-render.png',
    bullets: ['10.24 kWh & 20.48 kWh options', 'Standard install + smart meter', 'STC rebate handled for you'],
    cta: 'View EOFY Package',
  },
];

const PackagesPage = () => (
  <div className="min-h-screen">
    <SEO
      title="Packages — Fully Supplied & Installed | Supa Energy Hub"
      description="Browse Supa Energy Hub's fully supplied and installed packages — limited-time bundles covering equipment, install and rebate handling under one transparent price."
    />
    <Header />
    <main>
      {/* HERO */}
      <section className="pt-28 pb-12 sm:pb-16 relative overflow-hidden bg-gradient-to-br from-[#f6f8f3] via-white to-[#eaf2dc]">
        <div
          className="absolute inset-0 opacity-[0.15] pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle, #3D5A47 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        />
        <div className="absolute top-20 right-0 w-72 h-72 bg-[#B5D43B]/20 rounded-full blur-3xl pointer-events-none" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6" data-testid="packages-breadcrumb">
            <Link to="/" className="hover:text-[#3D5A47]">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-[#3D5A47] font-medium">Packages</span>
          </nav>
          <span className="inline-flex items-center gap-2 bg-[#B5D43B]/15 border border-[#B5D43B]/40 rounded-full px-3 py-1 mb-4">
            <Sparkles className="w-3.5 h-3.5 text-[#3D5A47]" />
            <span className="text-[11px] font-bold tracking-wider uppercase text-[#3D5A47]">
              Fully Supplied + Installed
            </span>
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#3D5A47] leading-[1.1] tracking-tight max-w-3xl">
            One transparent price.{' '}
            <span className="text-[#B5D43B] bg-[#3D5A47] px-2 rounded-md">Everything included.</span>
          </h1>
          <p className="text-base sm:text-lg text-gray-700 mt-5 max-w-2xl leading-relaxed">
            Our packages bundle the equipment, standard install, smart meter, paperwork and rebate handling
            under a single &ldquo;from&rdquo; price — so you know exactly what you&rsquo;re getting before we set foot on site.
          </p>
        </div>
      </section>

      {/* PACKAGE CARDS */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-6" data-testid="packages-grid">
            {PACKAGES.map((p) => {
              const BadgeIcon = p.badge.icon;
              return (
                <Link
                  key={p.slug}
                  to={`/packages/${p.slug}`}
                  className="group bg-white rounded-2xl border-2 border-[#B5D43B]/30 hover:border-[#B5D43B] hover:shadow-2xl transition-all overflow-hidden flex flex-col"
                  data-testid={`package-card-${p.slug}`}
                >
                  <div className="relative bg-gradient-to-br from-[#f6f8f3] to-[#eaf2dc] aspect-[16/10] flex items-center justify-center p-6">
                    <div
                      className="absolute inset-0 opacity-[0.18] pointer-events-none"
                      style={{
                        backgroundImage: 'radial-gradient(circle, #3D5A47 1px, transparent 1px)',
                        backgroundSize: '22px 22px',
                      }}
                    />
                    <img src={p.image} alt={p.name} className="relative h-44 w-auto object-contain drop-shadow-lg" />
                    <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-red-500 text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-md">
                      <BadgeIcon className="w-3 h-3" />
                      {p.badge.label}
                    </div>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <span className="text-[10px] uppercase tracking-widest text-[#B5D43B] font-bold mb-1">
                      {p.eyebrow}
                    </span>
                    <h2 className="text-xl font-bold text-[#3D5A47] group-hover:text-[#1a2820] transition-colors">
                      {p.name}
                    </h2>
                    <p className="text-sm text-gray-600 mt-2 leading-relaxed">{p.summary}</p>
                    <ul className="text-xs text-gray-600 mt-4 space-y-1.5">
                      {p.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-2">
                          <span className="text-[#B5D43B] font-bold">•</span> {b}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-auto pt-6 border-t border-gray-100 flex items-end justify-between">
                      <div>
                        <p className="text-2xl font-black text-[#3D5A47] leading-tight">{p.fromPrice}</p>
                        <p className="text-[11px] text-gray-500">{p.suffix}</p>
                      </div>
                      <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#3D5A47] group-hover:text-[#B5D43B]">
                        {p.cta} <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}

            {/* "More coming" placeholder card */}
            <div className="rounded-2xl border-2 border-dashed border-gray-200 p-8 flex flex-col items-center justify-center text-center bg-[#f8faf5]" data-testid="package-card-coming-soon">
              <div className="w-14 h-14 rounded-full bg-[#B5D43B]/20 flex items-center justify-center mb-3">
                <Battery className="w-6 h-6 text-[#3D5A47]" />
              </div>
              <h3 className="font-bold text-[#3D5A47]">More Packages Coming Soon</h3>
              <p className="text-sm text-gray-500 mt-2 max-w-xs">
                Solar + battery combo packages, heat pump packages and EV charger bundles are on the way.
                Sign up for early access.
              </p>
              <Link to="/contact" className="mt-4">
                <Button variant="outline" className="border-[#3D5A47]/20 text-[#3D5A47] hover:bg-white gap-2 text-sm" data-testid="packages-coming-soon-cta">
                  Notify Me <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CQ7 PACKAGES — Fox ESS modular range */}
      <section className="py-16 bg-[#f8faf5] border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
            <div>
              <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">
                Fox ESS CQ7 Range · 7 capacity options
              </span>
              <h2 className="heading-2 text-[#3D5A47]">Fox ESS CQ7 Battery Packages</h2>
              <p className="text-gray-600 mt-2 max-w-2xl text-sm">
                Modular high-voltage LFP batteries from 6.96 kWh up to 48.72 kWh. Every package includes the
                CQ7 battery + your choice of one of five Fox ESS hybrid inverters (5/8/10 kW single-phase or 10/15 kW three-phase),
                fully supplied and installed.
              </p>
            </div>
            <Link to="/fox-ess-battery-packages" className="text-sm font-medium text-[#3D5A47] hover:text-[#B5D43B] inline-flex items-center gap-1" data-testid="foxess-view-all-link">
              View all Fox ESS packages <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="cq7-packages-grid">
            {CQ7_PACKAGES.map((p) => (
              <Link
                key={p.slug}
                to={`/packages/${p.slug}`}
                className="group bg-white rounded-2xl border-2 border-[#B5D43B]/20 hover:border-[#B5D43B] hover:shadow-xl transition-all overflow-hidden flex flex-col"
                data-testid={`cq7-package-card-${p.slug}`}
              >
                <div className="bg-gradient-to-br from-[#3D5A47] to-[#1a2820] p-5 text-white relative overflow-hidden">
                  <div
                    className="absolute inset-0 opacity-[0.08] pointer-events-none"
                    style={{ backgroundImage: 'radial-gradient(circle, #B5D43B 1px, transparent 1px)', backgroundSize: '20px 20px' }}
                  />
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-1">
                      <Battery className="w-4 h-4 text-[#B5D43B]" />
                      <span className="text-[10px] uppercase tracking-widest text-[#B5D43B] font-bold">Fox ESS CQ7</span>
                    </div>
                    <p className="text-4xl font-black leading-none">{p.kwh}<span className="text-lg text-white/60"> kWh</span></p>
                    <p className="text-xs text-white/70 mt-2">{p.config}</p>
                  </div>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <p className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">Starting from</p>
                  <p className="text-3xl font-black text-[#3D5A47] leading-tight mt-0.5">{fmtAUD(startingPrice(p))}</p>
                  <p className="text-[11px] text-gray-500 mt-1">installed after BSTC rebate*</p>
                  <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-xs">
                    <span className="inline-flex items-center gap-1 text-gray-600">
                      <Zap className="w-3 h-3" /> 5 inverter options
                    </span>
                    <span className="inline-flex items-center gap-1 text-[#3D5A47] font-semibold group-hover:text-[#B5D43B]">
                      View <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-6">
            *Prices assume standard installation and customer assignment of the applicable BSTC rebate to Supa Energy Hub. Additional site-specific costs may apply.
          </p>
        </div>
      </section>

      {/* GOODWE G20 PACKAGES */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
            <div>
              <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">
                GoodWe ESA G20 Range · 15 capacity options
              </span>
              <h2 className="heading-2 text-[#3D5A47]">GoodWe ESA G20 Battery Packages</h2>
              <p className="text-gray-600 mt-2 max-w-2xl text-sm">
                GoodWe ESA all-in-one battery system using stackable G20 LFP modules — from 5.12 kWh up to 49.92 kWh.
                Each package includes your choice of GoodWe ESA hybrid inverter (single or three-phase), fully supplied and installed.
              </p>
            </div>
            <Link to="/goodwe-battery-packages" className="text-sm font-medium text-[#3D5A47] hover:text-[#B5D43B] inline-flex items-center gap-1" data-testid="goodwe-view-all-link">
              View all GoodWe packages <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="goodwe-packages-grid">
            {[...GOODWE_PACKAGES_512.slice(0, 4), ...GOODWE_PACKAGES_832.slice(0, 4)].map((p) => (
              <Link
                key={p.slug}
                to={`/${p.slug}`}
                className="group bg-white rounded-2xl border-2 border-[#B5D43B]/20 hover:border-[#B5D43B] hover:shadow-xl transition-all overflow-hidden flex flex-col"
                data-testid={`goodwe-package-card-${p.slug}`}
              >
                <div className="bg-gradient-to-br from-[#1a2820] to-[#3D5A47] p-5 text-white relative overflow-hidden">
                  <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, #B5D43B 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-1">
                      <Battery className="w-4 h-4 text-[#B5D43B]" />
                      <span className="text-[10px] uppercase tracking-widest text-[#B5D43B] font-bold">GoodWe G20 · {p.moduleType} kWh</span>
                    </div>
                    <p className="text-4xl font-black leading-none">{p.kwh.toFixed(2)}<span className="text-lg text-white/60"> kWh</span></p>
                    <p className="text-xs text-white/70 mt-2">{p.modules} × {p.moduleType} kWh module{p.modules > 1 ? 's' : ''}</p>
                  </div>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <p className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">Starting from</p>
                  <p className="text-3xl font-black text-[#3D5A47] leading-tight mt-0.5">{goodweFmt(goodweStart(p))}</p>
                  <p className="text-[11px] text-gray-500 mt-1">installed after BSTC discount*</p>
                  <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-xs">
                    <span className="inline-flex items-center gap-1 text-gray-600"><Zap className="w-3 h-3" /> ESA inverter options</span>
                    <span className="inline-flex items-center gap-1 text-[#3D5A47] font-semibold group-hover:text-[#B5D43B]">View <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-6">
            *Prices are customer out-of-pocket after estimated BSTC discount, based on standard installation. <Link to="/goodwe-battery-packages" className="underline hover:text-[#3D5A47]">See all 15 GoodWe packages →</Link>
          </p>
        </div>
      </section>

      {/* DEYE AI-W5.1 PACKAGES */}
      <section className="py-16 bg-[#f8faf5] border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
            <div>
              <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">
                Deye AI-W5.1 Range · 6 capacity options
              </span>
              <h2 className="heading-2 text-[#3D5A47]">Deye AI-W5.1 Battery Packages</h2>
              <p className="text-gray-600 mt-2 max-w-2xl text-sm">
                Deye AI-W5.1 all-in-one battery system using stackable AI-W5.1-B LFP modules — from 5.12 kWh up to 30.72 kWh.
                Each package includes your choice of Deye hybrid inverter (single or three-phase), a PDU base and a smart meter, fully supplied and installed.
              </p>
            </div>
            <Link to="/deye-battery-packages" className="text-sm font-medium text-[#3D5A47] hover:text-[#B5D43B] inline-flex items-center gap-1" data-testid="deye-view-all-link">
              View all Deye packages <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="deye-packages-grid">
            {DEYE_PACKAGES.map((p) => (
              <Link
                key={p.slug}
                to={`/${p.slug}`}
                className="group bg-white rounded-2xl border-2 border-[#B5D43B]/20 hover:border-[#B5D43B] hover:shadow-xl transition-all overflow-hidden flex flex-col"
                data-testid={`deye-package-card-${p.slug}`}
              >
                <div className="bg-gradient-to-br from-[#1a2820] to-[#3D5A47] p-5 text-white relative overflow-hidden">
                  <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, #B5D43B 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-1">
                      <Battery className="w-4 h-4 text-[#B5D43B]" />
                      <span className="text-[10px] uppercase tracking-widest text-[#B5D43B] font-bold">Deye AI-W5.1</span>
                    </div>
                    <p className="text-4xl font-black leading-none">{p.kwh.toFixed(2)}<span className="text-lg text-white/60"> kWh</span></p>
                    <p className="text-xs text-white/70 mt-2">{p.modules} × AI-W5.1-B module{p.modules > 1 ? 's' : ''}</p>
                  </div>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <p className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">Starting from</p>
                  <p className="text-3xl font-black text-[#3D5A47] leading-tight mt-0.5">{deyeFmt(deyeStart(p))}</p>
                  <p className="text-[11px] text-gray-500 mt-1">installed after BSTC discount*</p>
                  <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-xs">
                    <span className="inline-flex items-center gap-1 text-gray-600"><Zap className="w-3 h-3" /> 4 inverter options</span>
                    <span className="inline-flex items-center gap-1 text-[#3D5A47] font-semibold group-hover:text-[#B5D43B]">View <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-6">
            *Prices are customer out-of-pocket after estimated BSTC discount, based on standard installation. <Link to="/deye-battery-packages" className="underline hover:text-[#3D5A47]">See all 6 Deye packages →</Link>
          </p>
        </div>
      </section>

      {/* HOW PACKAGES DIFFER */}
      <section className="py-16 bg-[#f8faf5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-3">
            Packages vs Products
          </span>
          <h2 className="heading-2 text-[#3D5A47] mb-4">What&rsquo;s the difference?</h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-sm">
            Browse our{' '}
            <Link to="/products/batteries" className="text-[#3D5A47] font-semibold underline hover:text-[#B5D43B]">
              Products section
            </Link>{' '}
            if you want to compare the technical specs of each battery, heat pump or solar component on its own.
            Browse <strong>Packages</strong> here if you want a fully supplied + installed bundle at one transparent
            &ldquo;from&rdquo; price — including paperwork, smart meter and rebate handling.
          </p>
        </div>
      </section>
    </main>
    <Footer />
  </div>
);

export default PackagesPage;
